import React from "react";
import * as axios from "axios";
import {Button} from "@material-ui/core";
import "./styles.css";
import {DAY_NAMES, formatDate, STATUS_LABELS, STATUS_CLASSES} from "./shiftUtils";

const baseUrl = 'https://api.appetito.ro/';

// Parses a "YYYY-MM-DD" key (what the backend sends) into a local Date —
// not `new Date(str)`, which parses as UTC midnight and can land on the
// wrong calendar day once converted to local time for display.
function parseDateKey(key) {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Each deliverer's `days` are already sorted best-to-worst-orders by the
// backend, counted ones (up to countedDaysPerPeriod) first, so the table
// only needs to know how many day columns to render — the widest row sets
// it, everyone else's shorter row just leaves the trailing cells blank.
export default class Bonus extends React.Component {
  state = {
    offset: 0,
    periodStart: null,
    periodEnd: null,
    countedDaysPerPeriod: 7,
    deliverers: [],
    targets: [],
    loading: true,
  };

  componentDidMount() {
    this.getBonus();
    this.getTargets();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.offset !== this.state.offset) {
      this.getBonus();
    }
  }

  getBonus = async () => {
    this.setState({loading: true});
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/deliverer-bonus`, {
      params: {offset: this.state.offset},
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({
      periodStart: response.data.period_start,
      periodEnd: response.data.period_end,
      countedDaysPerPeriod: response.data.counted_days_per_period || 7,
      deliverers: response.data.deliverers || [],
      loading: false,
    });
  };

  getTargets = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/targets`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    const targets = (response.data || []).slice().sort((a, b) => a.min_delivered_orders - b.min_delivered_orders);
    this.setState({targets});
  };

  // The bonus a deliverer earned is the target amount of the highest
  // threshold their counted total reaches — targets is sorted ascending by
  // min_delivered_orders, so the last one still met wins.
  bonusAmountFor = (total) => {
    const {targets} = this.state;
    let amount = 0;
    for (const target of targets) {
      if (total >= target.min_delivered_orders) {
        amount = target.target_amount;
      }
    }
    return amount;
  };

  goToPreviousPeriod = () => this.setState({offset: this.state.offset - 1});

  goToNextPeriod = () => this.setState({offset: this.state.offset + 1});

  render() {
    const {periodStart, periodEnd, countedDaysPerPeriod, deliverers, loading} = this.state;
    const maxDays = deliverers.reduce((max, d) => Math.max(max, d.days.length), 0);
    const columns = Array.from({length: maxDays}, (_, i) => i);
    // The 7th counted column (index countedDaysPerPeriod - 1) gets a thicker
    // right border — everything past it is extra shifts that didn't make
    // this deliverer's counted-7, shown for visibility but not counted.
    const boundaryIndex = countedDaysPerPeriod - 1;

    return (
      <div className="deliverers-page deliverers-page-scroll">
        {loading && (
          <div className="deliverers-fullscreen-loader">
            <div className="loader"/>
          </div>
        )}
        <div className="deliverers-section">
          <div className="deliverers-section-header">
            <h3 className="deliverers-section-title">Bonus</h3>
          </div>

          <div className="deliverers-scheduler-nav">
            <Button onClick={this.goToPreviousPeriod}>&lt; Perioada anterioară</Button>
            <span className="deliverers-scheduler-week-label">
              {periodStart && periodEnd ? `${formatDate(parseDateKey(periodStart))} – ${formatDate(parseDateKey(periodEnd))}` : ''}
            </span>
            <Button onClick={this.goToNextPeriod}>Perioada următoare &gt;</Button>
          </div>

          {!loading && deliverers.length === 0 ? (
            <div className="deliverers-empty-state">Niciun livrator eligibil pentru bonus în această perioadă.</div>
          ) : (
            <div className="deliverers-table-scroll">
              <table className="deliverers-table deliverers-bonus-table">
                <thead>
                  <tr>
                    <th>Livrator</th>
                    <th className="deliverers-table-center">Total</th>
                    {columns.map((i) => (
                      <th
                        key={i}
                        className={`deliverers-table-center${i === boundaryIndex ? ' deliverers-bonus-boundary' : ''}`}
                      >
                        Ziua {i + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deliverers.map((d) => {
                    const bonusAmount = this.bonusAmountFor(d.total_delivered_orders);
                    return (
                    <tr key={d.deliverer_id} className={d.deleted ? 'deliverers-row-deleted' : ''}>
                      <td>{d.name}</td>
                      <td className="deliverers-table-center">
                        <strong>{d.total_delivered_orders}</strong>
                        <div className={`deliverers-bonus-total-amount${bonusAmount > 0 ? ' deliverers-bonus-total-amount-reached' : ' deliverers-bonus-total-amount-none'}`}>
                          {bonusAmount > 0 ? `${bonusAmount} lei` : 'Fără bonus'}
                        </div>
                      </td>
                      {columns.map((i) => {
                        const boundaryClass = i === boundaryIndex ? ' deliverers-bonus-boundary' : '';
                        const day = d.days[i];
                        if (!day) {
                          return <td key={i} className={boundaryClass.trim()}/>;
                        }
                        const date = parseDateKey(day.date);
                        return (
                          <td
                            key={i}
                            className={`deliverers-bonus-day-cell${day.counted ? '' : ' deliverers-bonus-day-uncounted'}${boundaryClass}`}
                          >
                            <div className="deliverers-bonus-day-date">
                              {DAY_NAMES[date.getDay()].slice(0, 3)} {formatDate(date)}
                            </div>
                            <span className={`deliverers-scheduler-status ${STATUS_CLASSES[day.status] || ''}`}>
                              {STATUS_LABELS[day.status] || day.status}
                            </span>
                            <div className="deliverers-bonus-day-orders">{day.delivered_orders} comenzi</div>
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }
}
